import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { UserRole } from "@/lib/types";
import { Building2 } from "lucide-react";
import logo from '@/assets/staymiddle-logo.png';

export function Footer() {
  const { user } = useAuth();

  // User-specific links based on role
  const getRoleLinks = () => {
    if (!user) return null;

    switch (user.role) {
      case UserRole.MANAGER:
        return (
          <div className="space-y-2">
            <h3 className="font-medium">Manager Links</h3>
            <ul className="space-y-1">
              <li>
                <Link href="/manager/dashboard">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Dashboard
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/manager/properties">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    My Properties
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/manager/bookings">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Bookings
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/manager/earnings">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Earnings
                  </div>
                </Link>
              </li>
            </ul>
          </div>
        );
      case UserRole.AGENT:
        return (
          <div className="space-y-2">
            <h3 className="font-medium">Agent Links</h3>
            <ul className="space-y-1">
              <li>
                <Link href="/agent/dashboard">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Dashboard
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/agent/properties">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Properties
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/agent/bookings">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Bookings
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/agent/links">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    My PropertyLinks
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/agent/commissions">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Commissions
                  </div>
                </Link>
              </li>
            </ul>
          </div>
        );
      case UserRole.ADMIN:
        return (
          <div className="space-y-2">
            <h3 className="font-medium">Admin Links</h3>
            <ul className="space-y-1">
              <li>
                <Link href="/admin/dashboard">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Dashboard
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/admin/users">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Manage Users
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/admin/properties">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Manage Properties
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/admin/bookings">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Bookings
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/admin/payments">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Payments
                  </div>
                </Link>
              </li>
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <footer className="border-t py-8 md:py-12">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and About */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-white flex items-center justify-center">
                <img 
                  src={logo} 
                  alt="StayMiddle Logo" 
                  className="h-10 w-auto" 
                />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              The premium property rental marketplace connecting property managers with rental agents and guests in Dubai.
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com/StayMiddleApp" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg
                  className="h-5 w-5 text-muted-foreground hover:text-primary"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a href="https://instagram.com/StayMiddleApp" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg
                  className="h-5 w-5 text-muted-foreground hover:text-primary"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a href="https://twitter.com/StayMiddleApp" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <svg
                  className="h-5 w-5 text-muted-foreground hover:text-primary"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="https://snapchat.com/add/StayMiddleApp" target="_blank" rel="noopener noreferrer" aria-label="Snapchat">
                <svg className="h-5 w-5 text-muted-foreground hover:text-primary" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2C7.03 2 3 6.03 3 11c0 4.97 4.03 9 9 9s9-4.03 9-9c0-4.97-4.03-9-9-9zm4.93 13.36c-.13.13-.29.2-.47.2-.07 0-.14-.01-.21-.04-.97-.36-1.97-.55-3.25-.55s-2.28.19-3.25.55c-.23.09-.5.03-.67-.16-.17-.21-.46-.1-.68.13-.27.27-.53.41-.78.13-.23.41-.32.65-.19.23.13.32.41.19.65-.11.19-.22.39-.32.59.8-.22 1.7-.34 2.67-.34s1.87.12 2.67.34c-.1-.2-.21-.4-.32-.59-.13-.23-.04-.52.19-.65.24-.13.52-.04.65.19.14.25.28.51.41.78.11.22.07.49-.1.68zm-4.93-2.36c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm6.5 2.5c-.28.28-.66.36-1.01.22-1.13-.44-2.36-.68-3.49-.68s-2.36.24-3.49.68c-.35.14-.73.06-1.01-.22-.28-.28-.36-.66-.22-1.01.44-1.13.68-2.36.68-3.49s-.24-2.36-.68-3.49c-.14-.35-.06-.73.22-1.01.28-.28.66-.36 1.01-.22 1.13.44 2.36.68 3.49.68s2.36-.24 3.49-.68c.35-.14.73-.06 1.01.22.28.28.36.66.22 1.01-.44 1.13-.68 2.36-.68 3.49s.24 2.36.68 3.49c.14.35.06.73-.22 1.01z" />
                </svg>
              </a>
              <a href="https://youtube.com/@StayMiddleApp" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <svg className="h-5 w-5 text-muted-foreground hover:text-primary" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21.8 8.001a2.75 2.75 0 0 0-1.93-1.95C18.01 6 12 6 12 6s-6.01 0-7.87.051A2.75 2.75 0 0 0 2.2 8.001C2 9.87 2 12 2 12s0 2.13.2 3.999a2.75 2.75 0 0 0 1.93 1.95C5.99 18 12 18 12 18s6.01 0 7.87-.051a2.75 2.75 0 0 0 1.93-1.95C22 14.13 22 12 22 12s0-2.13-.2-3.999zM10 15.5v-7l6 3.5-6 3.5z" />
                </svg>
              </a>
              <a href="https://tiktok.com/@StayMiddleApp" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg className="h-5 w-5 text-muted-foreground hover:text-primary" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12.75 2h2.25a.75.75 0 0 1 .75.75v2.25a3.75 3.75 0 0 0 3.75 3.75h1.5a.75.75 0 0 1 .75.75v2.25a.75.75 0 0 1-.75.75h-1.5a6.75 6.75 0 1 1-6.75-6.75.75.75 0 0 1 .75.75v2.25a.75.75 0 0 1-.75.75 3.75 3.75 0 1 0 3.75 3.75.75.75 0 0 1 .75-.75h2.25a.75.75 0 0 1 .75.75A6.75 6.75 0 1 1 12.75 2z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Main links */}
          <div className="space-y-2">
            <h3 className="font-medium">Explore</h3>
            <ul className="space-y-1">
              <li>
                <Link href="/properties">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Browse Properties
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/how-it-works">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    How It Works
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/pricing">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Pricing
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/contact">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Contact Us
                  </div>
                </Link>
              </li>
            </ul>
          </div>

          {/* Role-specific links */}
          {getRoleLinks() || (
            <div className="space-y-2">
              <h3 className="font-medium">Join Us</h3>
              <ul className="space-y-1">
                <li>
                  <Link href="/auth?tab=register&role=manager">
                    <div className="text-muted-foreground hover:text-primary cursor-pointer">
                      List Your Property
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/auth?tab=register&role=agent">
                    <div className="text-muted-foreground hover:text-primary cursor-pointer">
                      Become an Agent
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/auth?tab=register">
                    <div className="text-muted-foreground hover:text-primary cursor-pointer">
                      Create Account
                    </div>
                  </Link>
                </li>
                <li>
                  <Link href="/auth?tab=login">
                    <div className="text-muted-foreground hover:text-primary cursor-pointer">
                      Sign In
                    </div>
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Legal links */}
          <div className="space-y-2">
            <h3 className="font-medium">Legal</h3>
            <ul className="space-y-1">
              <li>
                <Link href="/terms">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Terms of Service
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/privacy">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Privacy Policy
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/cookies">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    Cookie Policy
                  </div>
                </Link>
              </li>
              <li>
                <Link href="/faq">
                  <div className="text-muted-foreground hover:text-primary cursor-pointer">
                    FAQ
                  </div>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
          <p>© 2025 StayMiddle. All rights reserved.</p>
          
        </div>
      </div>
    </footer>
  );
}